/**
 * @fileoverview Runner Difficulty Card Component
 * @description Card for selecting game difficulty in Endless Runner
 */

import React, { memo } from "react";
import { View, Text, TouchableOpacity, StyleSheet } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { RunnerDifficulty, RunnerDifficultyConfig } from "../types";

interface RunnerDifficultyCardProps {
  config: RunnerDifficultyConfig;
  bestScore: number;
  gamesPlayed: number;
  /** Minimum points (participation) */
  points?: number;
  /** Maximum points (participation + new highscore bonus) */
  maxPoints?: number;
  isWeekendMode?: boolean;
  onPress: (difficulty: RunnerDifficulty) => void;
  disabled?: boolean;
}

/**
 * RunnerDifficultyCard Component
 */
export const RunnerDifficultyCard = memo<RunnerDifficultyCardProps>(
  ({
    config,
    bestScore,
    gamesPlayed,
    points,
    maxPoints,
    isWeekendMode = false,
    onPress,
    disabled = false,
  }) => {
    const colors = useThemeColors();

    const handlePress = () => {
      if (!disabled) {
        onPress(config.difficulty);
      }
    };

    return (
      <View
        style={[
          styles.container,
          { backgroundColor: colors.surface, borderColor: `${config.color}40` },
          disabled && styles.disabled,
        ]}
      >
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.titleRow}>
            <View style={[styles.dot, { backgroundColor: config.color }]} />
            <Text style={[styles.title, { color: colors.text }]}>
              {config.label}
            </Text>
          </View>
          <Text style={[styles.description, { color: colors.textSecondary }]}>
            {config.description}
          </Text>
        </View>

        {/* Stats */}
        <View style={styles.statsRow}>
          <View style={styles.statItem}>
            <Ionicons name="trophy" size={14} color="#F59E0B" />
            <Text style={[styles.statValue, { color: colors.text }]}>
              {bestScore > 0 ? bestScore.toLocaleString() : "-"}
            </Text>
            <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
              Record
            </Text>
          </View>

          <View style={styles.statDivider} />

          <View style={styles.statItem}>
            <Ionicons name="game-controller" size={14} color={colors.primary} />
            <Text style={[styles.statValue, { color: colors.text }]}>
              {gamesPlayed}
            </Text>
            <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
              Gespeeld
            </Text>
          </View>

          {isWeekendMode && points !== undefined && (
            <>
              <View style={styles.statDivider} />
              <View style={styles.statItem}>
                <Ionicons name="star" size={14} color="#22C55E" />
                <Text style={[styles.statValue, { color: "#22C55E" }]}>
                  {maxPoints ? `${points}-${maxPoints}` : points}
                </Text>
                <Text style={[styles.statLabel, { color: colors.textSecondary }]}>
                  punten
                </Text>
              </View>
            </>
          )}
        </View>

        {/* Speed Info */}
        <View style={styles.speedRow}>
          <View style={styles.speedItem}>
            <Ionicons name="speedometer" size={12} color={colors.textTertiary} />
            <Text style={[styles.speedText, { color: colors.textTertiary }]}>
              Start: {config.initialSpeed}x
            </Text>
          </View>
          <View style={styles.speedItem}>
            <Ionicons name="flash" size={12} color={colors.textTertiary} />
            <Text style={[styles.speedText, { color: colors.textTertiary }]}>
              Max: {config.maxSpeed}x
            </Text>
          </View>
        </View>

        {/* Start Button */}
        <TouchableOpacity
          onPress={handlePress}
          disabled={disabled}
          style={[
            styles.startButton,
            { backgroundColor: config.color },
            disabled && styles.startButtonDisabled,
          ]}
          accessibilityRole="button"
          accessibilityLabel={`Start ${config.label} spel`}
        >
          <Ionicons name="play" size={18} color="#FFFFFF" />
          <Text style={styles.startButtonText}>Start Spel</Text>
        </TouchableOpacity>
      </View>
    );
  }
);

RunnerDifficultyCard.displayName = "RunnerDifficultyCard";

const styles = StyleSheet.create({
  container: {
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    gap: 12,
  },
  disabled: {
    opacity: 0.5,
  },
  header: {
    gap: 4,
  },
  titleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  dot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
  },
  description: {
    fontSize: 13,
    marginLeft: 20,
  },
  statsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-around",
    paddingVertical: 8,
  },
  statItem: {
    alignItems: "center",
    gap: 4,
  },
  statValue: {
    fontSize: 16,
    fontWeight: "600",
  },
  statLabel: {
    fontSize: 11,
  },
  statDivider: {
    width: 1,
    height: 30,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  speedRow: {
    flexDirection: "row",
    justifyContent: "center",
    gap: 16,
  },
  speedItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  speedText: {
    fontSize: 11,
  },
  startButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 12,
    borderRadius: 12,
    gap: 8,
  },
  startButtonDisabled: {
    opacity: 0.5,
  },
  startButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});

